import { Injectable } from '@angular/core';
import { AppSettings } from '../../app.settings';
import { CredentialTransitions } from '../session/credential-transition';

@Injectable({
  providedIn: 'root'
})
export class TabStorageService {
  public static readonly JWT_TOKEN = 'JWTToken';
  public static readonly REFRESH_TOKEN = 'refreshToken';
  public static readonly COGNITO_ACCESS_TOKEN = 'CognitoIdentityServiceProvider.accessToken';
  public static readonly COGNITO_ID_TOKEN = 'CognitoIdentityServiceProvider.idToken';
  public static readonly INFRA_MGMT_TOKEN = 'infra-mgmt-token';
  public static readonly SELECTED_WELLSPACE = 'selected-wellspace';

  // Shared by static Restangular hooks and injected services in this tab only.
  public static readonly credentials = new CredentialTransitions(
    () => ({token: window.sessionStorage.getItem('JWTToken'), refreshToken: window.sessionStorage.getItem('refreshToken')}),
    pair => {
      const entries: Array<[string, string | null]> = [['JWTToken', pair.token], ['refreshToken', pair.refreshToken]];
      for (const [key, value] of entries) {
        if (value === null) { window.sessionStorage.removeItem(key); } else { window.sessionStorage.setItem(key, value); }
      }
    },
    () => AppSettings.getEndpoint(),
  );

  private static readonly AUTH_KEYS = [
    'JWTToken',
    'refreshToken',
    'CognitoIdentityServiceProvider.accessToken',
    'CognitoIdentityServiceProvider.idToken',
    'infra-mgmt-token',
  ];

  private static readonly AUTH_KEY_PREFIXES = [
    'CognitoIdentityServiceProvider',
  ];

  public readonly credentials = TabStorageService.credentials;

  private static credentialKey(key: string): boolean { return key === this.JWT_TOKEN || key === this.REFRESH_TOKEN; }

  public static setStorage(key: string, value: any): void {
    if (!key || value === undefined || value === null) {
      return;
    }

    if (value instanceof Object) {
      window.sessionStorage.setItem(key, JSON.stringify(value));
      if (this.credentialKey(key)) { this.credentials.changed(); }
      return;
    }

    window.sessionStorage.setItem(key, value);
    if (this.credentialKey(key)) { this.credentials.changed(); }
  }

  public static getStorage(key: string): string | null {
    return key ? window.sessionStorage.getItem(key) : null;
  }

  public static removeStorage(key: string): void {
    if (key) {
      window.sessionStorage.removeItem(key);
      if (this.credentialKey(key)) { this.credentials.changed(); }
    }
  }

  public static getKeys(): string[] {
    return Object.keys(window.sessionStorage);
  }

  public static hasCognitoIdToken(): boolean {
    return TabStorageService.getKeys().some(key =>
      key.startsWith('CognitoIdentityServiceProvider') && key.endsWith('idToken')
    );
  }

  public static clearCurrentTabSession(): void {
    TabStorageService.clearAuthStorage();
    TabStorageService.removeStorage(TabStorageService.SELECTED_WELLSPACE);
  }

  public static clearAuthStorage(): void {
    TabStorageService.AUTH_KEYS.forEach(key => TabStorageService.removeStorage(key));
    TabStorageService.getKeys()
      .filter(key => TabStorageService.AUTH_KEY_PREFIXES.some(prefix => key.startsWith(prefix)))
      .forEach(key => TabStorageService.removeStorage(key));
  }

  public setStorage(key: string, value: any): void {
    TabStorageService.setStorage(key, value);
  }

  public getStorage(key: string): string | null {
    return TabStorageService.getStorage(key);
  }

  public removeStorage(key: string): void {
    TabStorageService.removeStorage(key);
  }

  public clearCurrentTabSession(): void {
    TabStorageService.clearCurrentTabSession();
  }

  public clearAuthStorage(): void {
    TabStorageService.clearAuthStorage();
  }
}
